/**
 * キーワード能力システム
 *
 * キーワード能力の定義・判定・ヘルパー関数を提供する
 */

import type { PlayerNumber } from '../../types/player';
import type { CardData } from '../../types/card';

// ========================================
// 型定義
// ========================================

/** キーワード判定に必要な最小限のインターフェース */
export interface KeywordCheckable {
  keyword?: string | string[];
  keywordText?: string;
  effect?: string;
}

/** キーワード能力を持つカード */
export interface KeywordCard extends CardData {
  /** keywordはCardDataではstring、ここで配列形式も許容 */
  keywordArray?: string[];
  keywordText?: string;
}

/** フィールド上のモンスター */
export interface FieldMonster extends KeywordCard {
  uniqueId: string;
  owner?: PlayerNumber;
  currentAttack?: number;
  currentHp?: number;
  maxHp?: number;
  canAttack?: boolean;
  usedSkillThisTurn?: boolean;
  linkedTo?: string | null;
  linkedBonus?: { atk: number; hp: number };
  linkedEndPhaseDamage?: LinkedEndPhaseDamage[];
  soureiUsedThisTurn?: boolean;
  usedMiraiYochiThisTurn?: boolean;
  zankonUsed?: boolean;
  charges?: unknown[];
  statusEffects?: unknown[];
}

/** リンクダメージ情報 */
export interface LinkedEndPhaseDamage {
  damage: number;
  linkedWith: string;
  sourceCardName: string;
}

/** 魂結の効果定義 */
export interface KonketsuEffect {
  targetCondition: string;
  atkBonus: number;
  hpBonus: number;
  endPhaseDamage: number;
}

/** 壮麗の効果定義 */
export interface SoureiEffect {
  description: string;
  effectType: string;
}

/** スタックアイテム */
export interface StackItem {
  id: string;
  card: KeywordCard;
  player: PlayerNumber;
  chainPoint: ChainPointType;
  context: Record<string, unknown>;
  timestamp: number;
}

/** 禁忌カード検証結果 */
export interface KinkiValidationResult {
  valid: boolean;
  count: number;
  message: string;
}

/** リンク対象 */
export interface LinkTarget {
  monster: FieldMonster;
  slotIndex: number;
}

/** 未来予知モンスター */
export interface MiraiYochiMonster {
  monster: FieldMonster;
  slotIndex: number;
}

/** 威圧吼ステータス */
export interface IatsukoStatus {
  canDirect: boolean;
  reason: string;
}

/** 変幻身コスト範囲 */
export interface HengenmiCostRange {
  min: number;
  max: number;
}

/** フィールド配列 */
export type FieldArray = (FieldMonster | null)[];

/** チェーンポイントの種類 */
export type ChainPointType = typeof CHAIN_POINTS[keyof typeof CHAIN_POINTS];

/** キーワード能力の値の型 */
export type KeywordAbilityValue = typeof KEYWORD_ABILITIES[keyof typeof KEYWORD_ABILITIES];

/** 効果コンテキスト（深蝕用） */
export interface ShinsyokuContext {
  addLog: (message: string, type?: string) => void;
  currentPlayer: PlayerNumber;
  p1Field: FieldArray;
  p2Field: FieldArray;
  setP1Field: (fn: (prev: FieldArray) => FieldArray) => void;
  setP2Field: (fn: (prev: FieldArray) => FieldArray) => void;
  setP1Graveyard: (fn: (prev: KeywordCard[]) => KeywordCard[]) => void;
  setP2Graveyard: (fn: (prev: KeywordCard[]) => KeywordCard[]) => void;
}

/** 効果コンテキスト（残魂用） */
export interface ZankonContext {
  addLog: (message: string, type?: string) => void;
  currentPlayer: PlayerNumber;
  p1Field: FieldArray;
  p2Field: FieldArray;
  setP1Field: (fn: (prev: FieldArray) => FieldArray) => void;
  setP2Field: (fn: (prev: FieldArray) => FieldArray) => void;
  p1Graveyard: FieldMonster[];
  p2Graveyard: FieldMonster[];
  setP1Graveyard: (fn: (prev: FieldMonster[]) => FieldMonster[]) => void;
  setP2Graveyard: (fn: (prev: FieldMonster[]) => FieldMonster[]) => void;
  summoningSickness?: boolean;
}

/** ターゲット選択UI設定 */
export interface PendingMonsterTarget {
  message: string;
  targetPlayer: 'opponent' | 'self' | 'both';
  callback: (selectedIndex: number) => void;
}

// =============================================================================
// キーワード能力の定義
// =============================================================================

export const KEYWORD_ABILITIES = {
  // ルール明記済み（Phase 1）
  SETSUNA_EISHO: '刹那詠唱',      // 相手ターン発動可能（魔法カード）
  KINKI_CARD: '禁忌カード',        // デッキ1枚制限

  // 戦闘・シンプル系（Phase 1）
  SHISHOKU: '死触',                // ダメージ1点でも与えれば破壊
  DOKUSHIN: '毒侵',                // プレイヤーダメージ時に毒付与

  // 戦闘・ターン終了系（Phase 2）
  SHINSYOKU: '深蝕',               // エンドフェイズ攻撃力減少
  MASHOUHEKI: '魔障壁',            // 魔法無効化
  ZANKON: '残魂',                  // 破壊時効果

  // 召喚・コスト系（Phase 3）
  HENGENMI: '変幻身',              // 形態変化
  GIGEN: '犠現',                   // 生贄コスト軽減
  KONKETSU: '魂結',                // リンク効果
  KAKUSEI: '覚醒',                 // 条件強化

  // 固有効果（Phase 4）
  SOUREI: '壮麗',                  // 同名カード捨てて効果
  IATSUKO: '威圧吼',               // 攻撃時追加効果
  MIRAI_YOCHI: '未来予知',         // デッキ操作

  // 戦闘系（Phase 5）
  HIKOU: '飛行',                   // 空中戦キーワード
  TAIKU: '対空',                   // 対空キーワード

  // 深海系（Phase 6）
  SENKOU: '潜航',                  // 手札から深海領域へ
  SHINEN_NO_KIBA: '深淵の牙',      // 浮上時ダメージ
  KAITEI_NO_NEMURI: '海底の眠り',  // 深海領域中LP回復
  ANKOKU_SUIIKI: '暗黒水域',       // 深度上昇+1
} as const;

// 実装方式の分類
export const KEYWORD_IMPLEMENTATION_TYPE = {
  GAME_FLOW: 'game_flow',           // magic-spirit.jsx でゲームフロー変更
  DECK_BUILDING: 'deck_building',   // デッキ構築時の検証
  TRIGGER: 'trigger',               // トリガーシステム
  CONTINUOUS: 'continuous',         // 継続効果システム
  ACTION: 'action',                 // プレイヤーアクション
  SUMMON_COST: 'summon_cost',       // 召喚コスト計算
  STATE: 'state',                   // 状態管理
} as const;

// 各キーワードの実装方式マッピング
export const KEYWORD_IMPLEMENTATION = {
  [KEYWORD_ABILITIES.SETSUNA_EISHO]: KEYWORD_IMPLEMENTATION_TYPE.GAME_FLOW,
  [KEYWORD_ABILITIES.KINKI_CARD]: KEYWORD_IMPLEMENTATION_TYPE.DECK_BUILDING,
  [KEYWORD_ABILITIES.SHISHOKU]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.DOKUSHIN]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.SHINSYOKU]: KEYWORD_IMPLEMENTATION_TYPE.CONTINUOUS,
  [KEYWORD_ABILITIES.MASHOUHEKI]: KEYWORD_IMPLEMENTATION_TYPE.CONTINUOUS,
  [KEYWORD_ABILITIES.ZANKON]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.HENGENMI]: KEYWORD_IMPLEMENTATION_TYPE.ACTION,
  [KEYWORD_ABILITIES.GIGEN]: KEYWORD_IMPLEMENTATION_TYPE.SUMMON_COST,
  [KEYWORD_ABILITIES.KONKETSU]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.KAKUSEI]: KEYWORD_IMPLEMENTATION_TYPE.STATE,
  [KEYWORD_ABILITIES.SOUREI]: KEYWORD_IMPLEMENTATION_TYPE.ACTION,
  [KEYWORD_ABILITIES.IATSUKO]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.MIRAI_YOCHI]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,
  [KEYWORD_ABILITIES.HIKOU]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,  // 戦闘ロジック
  [KEYWORD_ABILITIES.TAIKU]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER,  // 戦闘ロジック

  // 深海系（Phase 6）
  [KEYWORD_ABILITIES.SENKOU]: KEYWORD_IMPLEMENTATION_TYPE.ACTION,         // 手札から深海領域へ潜航
  [KEYWORD_ABILITIES.SHINEN_NO_KIBA]: KEYWORD_IMPLEMENTATION_TYPE.TRIGGER, // 浮上時ダメージトリガー
  [KEYWORD_ABILITIES.KAITEI_NO_NEMURI]: KEYWORD_IMPLEMENTATION_TYPE.CONTINUOUS, // 深海領域中LP回復
  [KEYWORD_ABILITIES.ANKOKU_SUIIKI]: KEYWORD_IMPLEMENTATION_TYPE.CONTINUOUS,    // 深度上昇+1
};

// =============================================================================
// キーワード判定関数
// =============================================================================

/**
 * カードが指定したキーワード能力を持つか判定
 * @param {Object} card - カードオブジェクト
 * @param {string} keyword - キーワード能力（【】なし）
 * @returns {boolean} キーワードを持つかどうか
 */
export function hasKeyword(card: KeywordCard | null, keyword: string): boolean {
  if (!card) return false;

  const keywordWithBrackets = `【${keyword}】`;

  // CSVの「キーワード能力」フィールドをチェック
  // card.keyword は配列形式（cardManager.jsで変換済み）または文字列形式
  // ランタイムでは配列の場合もあるため、型アサーションを使用
  const keywordField = card.keyword as unknown as string | string[] | undefined;
  if (keywordField) {
    if (Array.isArray(keywordField)) {
      // 配列の場合: 【】なしのキーワードで検索
      if (keywordField.includes(keyword)) return true;
    } else if (typeof keywordField === 'string') {
      // 文字列の場合: 【】ありで検索
      if (keywordField.includes(keywordWithBrackets)) return true;
    }
  }

  // keywordText（表示用の元のテキスト）もチェック
  const keywordText = card.keywordText || '';
  if (keywordText.includes(keywordWithBrackets)) return true;

  // 効果テキスト内のキーワードもチェック
  const effectText = card.effect || '';
  if (effectText.includes(keywordWithBrackets)) return true;

  return false;
}

/**
 * カードが持つキーワード能力一覧を取得
 * @param {Object} card - カードオブジェクト
 * @returns {string[]} キーワード能力の配列
 */
export function getCardKeywords(card: KeywordCard | null): string[] {
  const keywords: string[] = [];
  for (const value of Object.values(KEYWORD_ABILITIES)) {
    if (hasKeyword(card, value)) {
      keywords.push(value);
    }
  }
  return keywords;
}

/**
 * 刹那詠唱を持つ魔法カードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function isSetsunaMagic(card: KeywordCard | null): boolean {
  if (!card) return false;
  if (card.type !== 'magic') return false;
  return hasKeyword(card, KEYWORD_ABILITIES.SETSUNA_EISHO);
}

/**
 * 禁忌カードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function isKinkiCard(card: KeywordCard | null): boolean {
  if (!card) return false;
  return hasKeyword(card, KEYWORD_ABILITIES.KINKI_CARD);
}

// =============================================================================
// 刹那詠唱関連
// =============================================================================

/**
 * 刹那詠唱のコストを計算（通常コストと同じ）
 * @param {Object} card - カードオブジェクト
 * @returns {number} 刹那詠唱時のコスト
 */
export function getSetsunaCost(card: KeywordCard | null): number {
  if (!card) return 0;
  return card.cost || 0;
}

/**
 * 刹那詠唱で発動可能なカードをフィルタ
 * @param {Object[]} hand - 手札の配列
 * @param {number} activeSP - 使用可能なSP
 * @returns {Object[]} 発動可能なカードの配列
 */
export function getActivatableSetsunaMagics(hand: KeywordCard[] | null, activeSP: number): KeywordCard[] {
  if (!hand || !Array.isArray(hand)) return [];

  return hand.filter(card => {
    if (!isSetsunaMagic(card)) return false;
    const cost = getSetsunaCost(card);
    return activeSP >= cost;
  });
}

// =============================================================================
// 禁忌カード関連
// =============================================================================

/**
 * デッキ内の禁忌カード枚数をカウント
 * @param {Object[]} deck - デッキの配列
 * @returns {number} 禁忌カードの枚数
 */
export function countKinkiCards(deck: KeywordCard[] | null): number {
  if (!deck || !Array.isArray(deck)) return 0;
  return deck.filter(card => isKinkiCard(card)).length;
}

/**
 * デッキが禁忌カードルールに違反していないか検証
 * @param {Object[]} deck - デッキの配列
 * @returns {{ valid: boolean, count: number, message: string }}
 */
export function validateKinkiCards(deck: KeywordCard[] | null): KinkiValidationResult {
  const count = countKinkiCards(deck);
  const valid = count <= 1;
  const message = valid
    ? '禁忌カードルール: OK'
    : `禁忌カードルール違反: ${count}枚（上限1枚）`;

  return { valid, count, message };
}

// =============================================================================
// チェーンポイントシステム（刹那詠唱の発動タイミング）
// =============================================================================

/**
 * チェーンポイントの種類
 * 刹那詠唱カードが発動可能なタイミングを定義
 */
export const CHAIN_POINTS = {
  BATTLE_START: 'battle_start',       // バトルフェイズ開始時
  ATTACK_DECLARATION: 'attack_declaration', // 攻撃宣言時
  SUMMON: 'summon',                   // 召喚時（将来実装）
  MAGIC_ACTIVATION: 'magic_activation', // 魔法発動時（将来実装）
};

/**
 * チェーンポイントの表示名
 */
export const CHAIN_POINT_NAMES = {
  [CHAIN_POINTS.BATTLE_START]: 'バトルフェイズ開始',
  [CHAIN_POINTS.ATTACK_DECLARATION]: '攻撃宣言',
  [CHAIN_POINTS.SUMMON]: '召喚',
  [CHAIN_POINTS.MAGIC_ACTIVATION]: '魔法発動',
};

/**
 * スタックアイテムを作成
 * @param {Object} card - 発動するカード
 * @param {number} player - 発動プレイヤー（1 or 2）
 * @param {string} chainPoint - チェーンポイント種類
 * @param {Object} context - 追加コンテキスト（攻撃者情報など）
 * @returns {Object} スタックアイテム
 */
export function createStackItem(
  card: KeywordCard,
  player: PlayerNumber,
  chainPoint: ChainPointType,
  context: Record<string, unknown> = {}
): StackItem {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    card,
    player,
    chainPoint,
    context,
    timestamp: Date.now(),
  };
}

/**
 * スタックを解決（現在は1枚のみ、将来はLIFO）
 * @param {Object[]} stack - スタック配列
 * @param {Function} resolveEffect - 効果解決関数
 * @returns {Object[]} 解決後の空スタック
 */
export function resolveStack(
  stack: StackItem[],
  resolveEffect: (item: StackItem) => void
): StackItem[] {
  // Phase A: 1枚のみなので先頭を解決
  // Phase B: 逆順（LIFO）で解決
  const toResolve = [...stack].reverse();

  for (const item of toResolve) {
    resolveEffect(item);
  }

  return []; // スタックをクリア
}

// =============================================================================
// 【魂結】関連
// =============================================================================

/**
 * 【魂結】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasKonketsu(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.KONKETSU);
}

/**
 * 【魂結】の効果定義
 * カードIDごとのリンク効果を定義
 */
export const KONKETSU_EFFECTS = {
  // C0000333: 深みの儀式者 - 双方攻撃力+100
  C0000333: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 100,
    hpBonus: 0,
    endPhaseDamage: 0,
  },
  // C0000334: クラディオム - 双方攻撃力+300
  C0000334: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 300,
    hpBonus: 0,
    endPhaseDamage: 0,
  },
  // C0000335: シスラゴン - 双方にターン終了時300ダメージ付与
  C0000335: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 0,
    hpBonus: 0,
    endPhaseDamage: 300,
  },
  // C0000336: ルミナクール - 双方HP+800
  C0000336: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 0,
    hpBonus: 800,
    endPhaseDamage: 0,
  },
  // C0000337: タラッサロス - 双方攻撃力+1000、ターン終了時800ダメージ付与
  C0000337: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 1000,
    hpBonus: 0,
    endPhaseDamage: 800,
  },
  // C0000340: 深海の支配者・ヴェルゼファール - 双方にターン終了時800ダメージ付与
  C0000340: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 0,
    hpBonus: 0,
    endPhaseDamage: 800,
  },
  // C0000526: レヴィアタン - 双方攻撃力+400
  C0000526: {
    targetCondition: 'ヴェルゼファール',
    atkBonus: 400,
    hpBonus: 0,
    endPhaseDamage: 0,
  },
};

/**
 * 【魂結】の効果定義を取得
 * @param {string} cardId - カードID
 * @returns {Object|null} 効果定義
 */
export function getKonketsuEffect(cardId: string): KonketsuEffect | null {
  return KONKETSU_EFFECTS[cardId as keyof typeof KONKETSU_EFFECTS] || null;
}

/**
 * フィールド上でリンク可能な対象を検索
 * @param {Object[]} field - フィールド配列
 * @param {string} targetCondition - 対象条件（名前に含む文字列）
 * @param {string} excludeUniqueId - 除外するカードのuniqueId（自分自身）
 * @returns {Object[]} リンク可能なモンスターの配列 [{monster, slotIndex}]
 */
export function findLinkableTargets(
  field: FieldArray,
  targetCondition: string,
  excludeUniqueId: string
): LinkTarget[] {
  const targets: LinkTarget[] = [];
  field.forEach((monster, idx) => {
    if (monster &&
        monster.uniqueId !== excludeUniqueId &&
        monster.name &&
        monster.name.includes(targetCondition) &&
        !monster.linkedTo) { // 既にリンク済みでない
      targets.push({ monster, slotIndex: idx });
    }
  });
  return targets;
}

/**
 * 【魂結】リンクを実行
 * 双方のモンスターにリンク効果を適用
 * @param {Object} sourceMonster - リンク元モンスター
 * @param {number} sourceSlotIndex - リンク元のスロットインデックス
 * @param {Object} targetMonster - リンク先モンスター
 * @param {number} targetSlotIndex - リンク先のスロットインデックス
 * @param {Object} effect - 効果定義（KONKETSU_EFFECTS）
 * @param {Function} setField - フィールド更新関数
 * @param {Function} addLog - ログ関数
 * @returns {boolean} 成功したかどうか
 */
export function executeKonketsuLink(
  sourceMonster: FieldMonster,
  sourceSlotIndex: number,
  targetMonster: FieldMonster,
  targetSlotIndex: number,
  effect: KonketsuEffect,
  setField: (fn: (prev: FieldArray) => FieldArray) => void,
  addLog: (message: string, type?: string) => void
): boolean {
  const { atkBonus, hpBonus, endPhaseDamage } = effect;

  setField(prev => {
    const newField = [...prev];

    // リンク元モンスターを更新
    const sourceEntry = newField[sourceSlotIndex];
    if (sourceEntry) {
      const source = { ...sourceEntry };
      source.linkedTo = targetMonster.uniqueId;
      source.linkedBonus = { atk: atkBonus, hp: hpBonus };
      source.currentAttack = (source.currentAttack ?? source.attack ?? 0) + atkBonus;
      source.maxHp = (source.maxHp ?? source.hp ?? 0) + hpBonus;
      source.currentHp = (source.currentHp ?? source.hp ?? 0) + hpBonus;

      if (endPhaseDamage > 0) {
        source.linkedEndPhaseDamage = [
          ...(source.linkedEndPhaseDamage || []),
          { damage: endPhaseDamage, linkedWith: targetMonster.uniqueId, sourceCardName: sourceMonster.name || '' }
        ];
      }
      newField[sourceSlotIndex] = source;
    }

    // リンク先モンスターを更新
    const targetEntry = newField[targetSlotIndex];
    if (targetEntry) {
      const target = { ...targetEntry };
      target.linkedTo = sourceMonster.uniqueId;
      target.linkedBonus = { atk: atkBonus, hp: hpBonus };
      target.currentAttack = (target.currentAttack ?? target.attack ?? 0) + atkBonus;
      target.maxHp = (target.maxHp ?? target.hp ?? 0) + hpBonus;
      target.currentHp = (target.currentHp ?? target.hp ?? 0) + hpBonus;

      if (endPhaseDamage > 0) {
        target.linkedEndPhaseDamage = [
          ...(target.linkedEndPhaseDamage || []),
          { damage: endPhaseDamage, linkedWith: sourceMonster.uniqueId, sourceCardName: sourceMonster.name || '' }
        ];
      }
      newField[targetSlotIndex] = target;
    }

    return newField;
  });

  // ログ出力
  const effects = [];
  if (atkBonus > 0) effects.push(`ATK+${atkBonus}`);
  if (hpBonus > 0) effects.push(`HP+${hpBonus}`);
  if (endPhaseDamage > 0) effects.push(`ターン終了時${endPhaseDamage}ダメージ`);

  addLog(`【魂結】${sourceMonster.name}と${targetMonster.name}がリンク！双方に${effects.join('、')}`, 'info');

  return true;
}

/**
 * リンク解除処理
 * モンスターが場を離れる時にリンク相手の効果を解除
 * @param {Object} leavingMonster - 場を離れるモンスター
 * @param {Function} setField - フィールド更新関数
 * @param {Function} addLog - ログ関数
 */
export function handleLinkBreak(
  leavingMonster: FieldMonster | null,
  setField: (fn: (prev: FieldArray) => FieldArray) => void,
  addLog: (message: string, type?: string) => void
): void {
  if (!leavingMonster || !leavingMonster.linkedTo) return;

  const linkedToId = leavingMonster.linkedTo;

  setField(prev => {
    return prev.map(monster => {
      if (monster && monster.uniqueId === linkedToId) {
        const updated = { ...monster };

        // リンクボーナスを解除
        const bonus = updated.linkedBonus || { atk: 0, hp: 0 };
        updated.currentAttack = Math.max(0, (updated.currentAttack || 0) - bonus.atk);

        // maxHPを減らす（currentHPはmaxHPを超えない限り維持）
        updated.maxHp = Math.max(1, (updated.maxHp ?? updated.hp ?? 0) - bonus.hp);
        if ((updated.currentHp ?? 0) > (updated.maxHp ?? 0)) {
          updated.currentHp = updated.maxHp;
        }

        // エンドフェイズダメージからこのリンクによるものを削除
        updated.linkedEndPhaseDamage = (updated.linkedEndPhaseDamage || [])
          .filter(d => d.linkedWith !== leavingMonster.uniqueId);

        // リンク状態をクリア
        updated.linkedTo = null;
        updated.linkedBonus = { atk: 0, hp: 0 };

        addLog(`${updated.name}のリンクが解除された（ATK-${bonus.atk}、maxHP-${bonus.hp}）`, 'info');

        return updated;
      }
      return monster;
    });
  });
}

/**
 * エンドフェイズのリンクダメージを処理
 * @param {Object[]} field - フィールド配列
 * @param {Function} setOpponentLife - 相手ライフ設定関数
 * @param {Function} addLog - ログ関数
 * @returns {number} 与えた総ダメージ
 */
export function processLinkEndPhaseDamage(
  field: FieldArray,
  setOpponentLife: (fn: (prev: number) => number) => void,
  addLog: (message: string, type?: string) => void
): number {
  let totalDamage = 0;

  field.forEach(monster => {
    if (monster && monster.linkedEndPhaseDamage && monster.linkedEndPhaseDamage.length > 0) {
      monster.linkedEndPhaseDamage.forEach(({ damage }) => {
        totalDamage += damage;
        addLog(`${monster.name}の【魂結】効果: 相手に${damage}ダメージ`, 'damage');
      });
    }
  });

  if (totalDamage > 0) {
    setOpponentLife(prev => Math.max(0, prev - totalDamage));
  }

  return totalDamage;
}

// =============================================================================
// 【深蝕】関連
// =============================================================================

/**
 * 【深蝕】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasShinsyoku(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.SHINSYOKU);
}

/**
 * 【深蝕】を持つカードのID一覧
 */
export const SHINSYOKU_CARD_IDS = [
  'C0000334', // クラディオム
  'C0000335', // シスラゴン
  'C0000336', // ルミナクール
  'C0000337', // タラッサロス
  'C0000340', // ヴェルゼファール
];

/**
 * 【深蝕】の効果を実行
 * 自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊
 * @param {Object} context - 効果コンテキスト
 * @param {string} sourceName - 効果発動元のカード名
 * @param {Function} setPendingMonsterTarget - ターゲット選択UI設定関数（オプション）
 * @returns {boolean} 効果を適用したかどうか
 */
export function executeShinsyokuEffect(
  context: ShinsyokuContext,
  sourceName: string,
  setPendingMonsterTarget: ((target: PendingMonsterTarget) => void) | null = null
): boolean {
  const { addLog, currentPlayer, p1Field, p2Field, setP1Field, setP2Field, setP1Graveyard, setP2Graveyard } = context;

  // 相手のフィールドを取得
  const opponentField = currentPlayer === 1 ? p2Field : p1Field;
  const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;
  const setOpponentGraveyard = currentPlayer === 1 ? setP2Graveyard : setP1Graveyard;

  // 相手モンスターを取得
  const validTargets = opponentField
    .map((m, idx) => ({ monster: m, index: idx }))
    .filter(({ monster }) => monster !== null);

  if (validTargets.length === 0) {
    addLog(`【深蝕】${sourceName}: 相手フィールドにモンスターがいません`, 'info');
    return false;
  }

  // ATKを500下げる処理
  const applyShinsyoku = (targetIndex: number): void => {
    const target = opponentField[targetIndex];
    if (!target) return;

    const newAtk = Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 500);
    addLog(`【深蝕】${sourceName}: ${target.name}の攻撃力を500ダウン（${newAtk}）`, 'damage');

    if (newAtk <= 0) {
      // ATKが0になったら破壊
      addLog(`${target.name}は攻撃力が0になり破壊された！`, 'damage');
      setOpponentGraveyard(prev => [...prev, target]);
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });
    } else {
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = { ...target, currentAttack: newAtk };
        return newField;
      });
    }
  };

  // 1体のみの場合は自動選択
  if (validTargets.length === 1) {
    applyShinsyoku(validTargets[0].index);
    return true;
  }

  // 複数いる場合は選択UI（またはフォールバックで最初のターゲット）
  if (setPendingMonsterTarget) {
    setPendingMonsterTarget({
      message: `【深蝕】攻撃力を500下げる相手モンスターを選択`,
      targetPlayer: 'opponent',
      callback: (selectedIndex) => {
        applyShinsyoku(selectedIndex);
      },
    });
    return true;
  }

  // フォールバック: 最初のターゲット
  applyShinsyoku(validTargets[0].index);
  return true;
}

// =============================================================================
// 【死触】関連
// =============================================================================

/**
 * 【死触】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasShishoku(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.SHISHOKU);
}

/**
 * 【死触】の対象カードID一覧
 * C0000409: 血涙の叫女バンシーディス
 * C0000426: 灰塵の怨念スペクトラグス
 */
export const SHISHOKU_CARD_IDS = ['C0000409', 'C0000426'];

/**
 * 【死触】効果を適用すべきか判定
 * ダメージが1以上であれば、相手モンスターを即座に破壊
 * @param {Object} attacker - 攻撃者モンスター
 * @param {number} damage - 与えたダメージ
 * @returns {boolean} 【死触】で破壊すべきかどうか
 */
export function shouldApplyShishoku(attacker: FieldMonster | null, damage: number): boolean {
  if (!attacker) return false;
  if (damage < 1) return false;
  return hasShishoku(attacker);
}

// =============================================================================
// 【魔障壁】関連
// =============================================================================

/**
 * 【魔障壁】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasMashouheki(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.MASHOUHEKI);
}

/**
 * 【魔障壁】の対象カードID一覧
 * C0000002: 触覚持ち粘液獣
 * C0000234: 撮影会のリリカ（既にMAGIC_NEGATIONで実装済み）
 */
export const MASHOUHEKI_CARD_IDS = ['C0000002', 'C0000234'];

// =============================================================================
// 【残魂】関連
// =============================================================================

/**
 * 【残魂】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasZankon(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.ZANKON);
}

/**
 * 【残魂】の対象カードID一覧
 * C0000320: 雷帝ヴォルトロン
 * C0000327: 雷嵐龍サンダーストーム・レックス
 */
export const ZANKON_CARD_IDS = ['C0000320', 'C0000327'];

/**
 * 【残魂】攻撃力減少値
 * 破壊時に攻撃力-1000で場に戻る
 */
export const ZANKON_ATK_REDUCTION = 1000;

/**
 * 【残魂】効果を実行
 * 破壊時に攻撃力-1000で場に戻る
 * @param {Object} context - 効果コンテキスト
 * @param {Object} destroyedMonster - 破壊されたモンスター
 * @returns {boolean} 効果を適用したかどうか
 */
export function executeZankonEffect(context: ZankonContext, destroyedMonster: FieldMonster | null): boolean {
  const { addLog, currentPlayer, p1Field, p2Field, setP1Field, setP2Field, p1Graveyard, p2Graveyard, setP1Graveyard, setP2Graveyard, summoningSickness = false } = context;

  if (!destroyedMonster || !hasZankon(destroyedMonster)) {
    return false;
  }

  // 破壊されたモンスターのオーナーを確認
  const monsterOwner = destroyedMonster.owner || currentPlayer;
  const myField = monsterOwner === 1 ? p1Field : p2Field;
  const setMyField = monsterOwner === 1 ? setP1Field : setP2Field;
  const myGraveyard = monsterOwner === 1 ? p1Graveyard : p2Graveyard;
  const setMyGraveyard = monsterOwner === 1 ? setP1Graveyard : setP2Graveyard;

  // 空きスロットを探す
  const emptySlot = myField.findIndex(slot => slot === null);
  if (emptySlot === -1) {
    addLog(`【残魂】${destroyedMonster.name}は場が満杯のため戻れなかった`, 'info');
    return false;
  }

  // 新しい攻撃力を計算（元の攻撃力から-1000）
  const baseAttack = destroyedMonster.attack || 0;
  const newAttack = Math.max(0, baseAttack - ZANKON_ATK_REDUCTION);

  // 攻撃力が0以下になる場合は戻れない
  if (newAttack <= 0) {
    addLog(`【残魂】${destroyedMonster.name}は攻撃力が0になるため戻れなかった`, 'info');
    return false;
  }

  // 墓地からカードを取り除く
  setMyGraveyard(prev => prev.filter(c => c.uniqueId !== destroyedMonster.uniqueId));

  // 復活したモンスターを作成
  const revivedMonster = {
    ...destroyedMonster,
    attack: newAttack,
    currentAttack: newAttack,
    currentHp: destroyedMonster.hp,
    maxHp: destroyedMonster.hp,
    canAttack: !summoningSickness, // 召喚酔い設定に依存
    usedSkillThisTurn: false,
    charges: [],
    statusEffects: [],
    zankonUsed: true, // 残魂は1回のみ
  };

  // フィールドに戻す
  setMyField(prev => {
    const newField = [...prev];
    newField[emptySlot] = revivedMonster;
    return newField;
  });

  addLog(`【残魂】${destroyedMonster.name}が攻撃力${newAttack}で場に戻った！`, 'info');
  return true;
}

// =============================================================================
// 【毒侵】関連
// =============================================================================

/**
 * 【毒侵】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasDokushin(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.DOKUSHIN);
}

/**
 * 【毒侵】の対象カードID一覧
 * C0000281: 毒使いカムラ
 * C0000283: 酸毒竜
 * C0000284: 白蛇の牙
 */
export const DOKUSHIN_CARD_IDS = ['C0000281', 'C0000283', 'C0000284'];

/**
 * 【毒侵】効果を適用すべきか判定
 * プレイヤーにダイレクトアタックが成功した時に毒状態を付与
 * @param {Object} attacker - 攻撃者モンスター
 * @param {number} damage - 与えたダメージ
 * @returns {boolean} 【毒侵】を適用すべきかどうか
 */
export function shouldApplyDokushin(attacker: FieldMonster | null, damage: number): boolean {
  if (!attacker) return false;
  if (damage < 1) return false;
  return hasDokushin(attacker);
}

// =============================================================================
// 【壮麗】関連
// =============================================================================

/**
 * 【壮麗】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasSourei(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.SOUREI);
}

/**
 * 【壮麗】の対象カードID一覧
 */
export const SOUREI_CARD_IDS = [
  'C0000345', // ヴォランティス・アルディオン → 相手モンスター効果無効化
  'C0000346', // ヴォランティス・セラヴェント → 【残魂】付与
  'C0000348', // ヴォランティス・ファルクェス → 2回攻撃可能
  'C0000359', // ヴォランティス・インフェルノ → 戦闘ダメージ1.5倍
  'C0000360', // ヴォランティス・テンペスト → 相手SP増加-1
  'C0000364', // ヴォランティス・エクリプス → 相手モンスター攻撃力半減
];

/**
 * 【壮麗】の効果定義（カードごと）
 */
export const SOUREI_EFFECTS = {
  C0000345: { description: '相手モンスター1体の効果を無効化', effectType: 'negate_effect' },
  C0000346: { description: '自分モンスター1体に【残魂】を付与', effectType: 'grant_zankon' },
  C0000348: { description: 'このターン2回攻撃可能', effectType: 'double_attack' },
  C0000359: { description: '次の戦闘ダメージ1.5倍', effectType: 'damage_boost' },
  C0000360: { description: '相手の次ターンSP増加を-1', effectType: 'sp_reduce' },
  C0000364: { description: '相手モンスター全体の攻撃力を半減', effectType: 'halve_attack' },
};

/**
 * 手札に同名カードがあるか確認
 * @param {Array} hand - 手札
 * @param {string} cardName - カード名
 * @param {string} excludeUniqueId - 除外するuniqueId（自分自身）
 * @returns {Object|null} 見つかったカード、または null
 */
export function findSameNameCardInHand(
  hand: KeywordCard[] | null,
  cardName: string,
  excludeUniqueId: string
): KeywordCard | null {
  if (!hand || !cardName) return null;
  return hand.find(c => c.name === cardName && (c as FieldMonster).uniqueId !== excludeUniqueId) || null;
}

/**
 * 【壮麗】発動可能か判定
 * @param {Object} monster - フィールド上のモンスター
 * @param {Array} hand - 手札
 * @returns {boolean}
 */
export function canActivateSourei(monster: FieldMonster | null, hand: KeywordCard[] | null): boolean {
  if (!monster || !hasSourei(monster)) return false;
  if (monster.soureiUsedThisTurn) return false; // 1ターン1回
  return findSameNameCardInHand(hand, monster.name || '', monster.uniqueId) !== null;
}

/**
 * 【壮麗】効果の定義を取得
 * @param {string} cardId - カードID
 * @returns {Object|null}
 */
export function getSoureiEffect(cardId: string): SoureiEffect | null {
  return SOUREI_EFFECTS[cardId as keyof typeof SOUREI_EFFECTS] || null;
}

// =============================================================================
// 【犠現】関連
// =============================================================================

/**
 * 【犠現】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasGigen(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.GIGEN);
}

/**
 * 【犠現】の対象カードID一覧
 * C0000353: ヴォランティス・エテルノス（コスト10）
 */
export const GIGEN_CARD_IDS = ['C0000353'];

/**
 * 【犠現】コスト軽減上限
 */
export const GIGEN_MAX_REDUCTION = 3;

/**
 * 【犠現】のコスト軽減を計算
 * @param {Object} sacrificeMonster - 生贄にするモンスター
 * @returns {number} コスト軽減量（最大3）
 */
export function calculateGigenReduction(sacrificeMonster: KeywordCard | null): number {
  if (!sacrificeMonster) return 0;
  const cost = sacrificeMonster.cost || 0;
  return Math.min(cost, GIGEN_MAX_REDUCTION);
}

/**
 * 【犠現】発動可能か判定
 * @param {Object} card - 召喚しようとするカード
 * @param {Array} field - 自分のフィールド
 * @returns {boolean}
 */
export function canActivateGigen(card: KeywordCard | null, field: FieldArray | null): boolean {
  if (!card || !hasGigen(card)) return false;
  // フィールドにモンスターがいるか
  const hasMonster = field ? field.some(m => m !== null) : false;
  return hasMonster;
}

// =============================================================================
// 【威圧吼】関連
// =============================================================================

/**
 * 【威圧吼】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasIatsuko(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.IATSUKO);
}

/**
 * 【威圧吼】の対象カードID一覧
 * C0000319: 嵐光の騎士
 */
export const IATSUKO_CARD_IDS = ['C0000319'];

/**
 * 【威圧吼】によりブロックを無視できるか判定
 * 相手モンスターが1体以下の場合、ブロックされずにダイレクトアタック可能
 * @param {Object} attacker - 攻撃者モンスター
 * @param {Array} opponentField - 相手フィールド
 * @returns {boolean} ブロックを無視できるかどうか
 */
export function canIgnoreBlockWithIatsuko(attacker: FieldMonster | null, opponentField: FieldArray): boolean {
  if (!attacker || !hasIatsuko(attacker)) return false;

  // 相手モンスターの数をカウント
  const opponentMonsterCount = opponentField.filter(m => m !== null).length;

  // 1体以下ならブロック無視可能
  return opponentMonsterCount <= 1;
}

/**
 * 【威圧吼】によるダイレクトアタック可能か判定
 * @param {Object} attacker - 攻撃者モンスター
 * @param {Array} opponentField - 相手フィールド
 * @returns {{ canDirect: boolean, reason: string }} ダイレクト可能かと理由
 */
export function getIatsukoDirectAttackStatus(attacker: FieldMonster | null, opponentField: FieldArray): IatsukoStatus {
  if (!attacker) {
    return { canDirect: false, reason: '' };
  }

  if (!hasIatsuko(attacker)) {
    return { canDirect: false, reason: '' };
  }

  const opponentMonsterCount = opponentField.filter(m => m !== null).length;

  if (opponentMonsterCount <= 1) {
    return {
      canDirect: true,
      reason: opponentMonsterCount === 0
        ? '【威圧吼】相手モンスターなし'
        : '【威圧吼】相手モンスター1体のみ - ブロック無視'
    };
  }

  return {
    canDirect: false,
    reason: `【威圧吼】相手モンスター${opponentMonsterCount}体 - ブロックされる`
  };
}

// =============================================================================
// 【変幻身】関連
// =============================================================================

/**
 * 【変幻身】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasHengenmi(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.HENGENMI);
}

/**
 * 【変幻身】の対象カードID一覧
 */
export const HENGENMI_CARD_IDS = [
  'C0000304', // 粘液獣・幼体（原始）
  'C0000305', // フレア・リザード（炎）
  'C0000306', // アクア・スネーク（水）
  'C0000307', // 輝くペガサス（光）
  'C0000308', // シャドウ・クロウ（闇）
  'C0000309', // 未来の雛鴉（未来）
  'C0000310', // 鉄槍の訓練兵（なし）
];

/**
 * 【変幻身】のSPコスト
 */
export const HENGENMI_SP_COST = 2;

/**
 * 【変幻身】の変化先コスト範囲を計算
 * @param {Object} monster - 変幻身モンスター
 * @returns {{ min: number, max: number }} コスト範囲
 */
export function getHengenmiCostRange(monster: KeywordCard | null): HengenmiCostRange {
  if (!monster) return { min: 0, max: 0 };
  const baseCost = monster.cost || 0;
  const targetCost = baseCost + HENGENMI_SP_COST; // 元コスト + SP2
  return {
    min: Math.max(1, targetCost - 1), // ±1
    max: targetCost + 1,
  };
}

/**
 * デッキから【変幻身】の変化先候補を取得
 * @param {Object} monster - 変幻身モンスター
 * @param {Array} deck - デッキ
 * @returns {Array} 変化先候補カードの配列
 */
export function getHengenmiTargets(monster: KeywordCard | null, deck: KeywordCard[] | null): KeywordCard[] {
  if (!monster || !deck || deck.length === 0) return [];
  if (!hasHengenmi(monster)) return [];

  const { min, max } = getHengenmiCostRange(monster);
  const attribute = monster.attribute;

  return deck.filter(card => {
    // モンスターカードのみ
    if (card.type !== 'monster') return false;
    // 同属性
    if (card.attribute !== attribute) return false;
    // コスト範囲内
    const cost = card.cost || 0;
    if (cost < min || cost > max) return false;
    // 自分自身は除外（同名カードも変化先になりうる）
    return true;
  });
}

/**
 * 【変幻身】発動可能か判定
 * @param {Object} monster - フィールド上のモンスター
 * @param {number} activeSP - 使用可能なSP
 * @param {Array} deck - デッキ
 * @param {number} phase - 現在のフェイズ (2 = メインフェイズ)
 * @returns {boolean}
 */
export function canActivateHengenmi(
  monster: KeywordCard | null,
  activeSP: number,
  deck: KeywordCard[] | null,
  phase: number
): boolean {
  if (!monster || !hasHengenmi(monster)) return false;
  if (phase !== 2) return false; // メインフェイズのみ
  if (activeSP < HENGENMI_SP_COST) return false; // SP不足
  // 変化先候補があるか
  const targets = getHengenmiTargets(monster, deck);
  return targets.length > 0;
}

// =============================================================================
// 【未来予知】関連
// =============================================================================

/**
 * 【未来予知】を持つカードか判定
 * @param {Object} card - カードオブジェクト
 * @returns {boolean}
 */
export function hasMiraiYochi(card: KeywordCard | null): boolean {
  return hasKeyword(card, KEYWORD_ABILITIES.MIRAI_YOCHI);
}

/**
 * 【未来予知】の対象カードID一覧
 * C0000316: 未来鴉の預言者
 */
export const MIRAI_YOCHI_CARD_IDS = ['C0000316'];

/**
 * 【未来予知】発動可能か判定
 * メインフェイズ中、1ターンに1度のみ発動可能
 * @param {Object} monster - フィールド上のモンスター
 * @param {number} phase - 現在のフェイズ (2 = メインフェイズ)
 * @returns {boolean}
 */
export function canActivateMiraiYochi(monster: FieldMonster | null, phase: number): boolean {
  if (!monster || !hasMiraiYochi(monster)) return false;
  if (phase !== 2) return false; // メインフェイズのみ
  if (monster.usedMiraiYochiThisTurn) return false; // 1ターン1回
  return true;
}

/**
 * フィールド上で【未来予知】発動可能なモンスターを検索
 * @param {Array} field - フィールド配列
 * @param {number} phase - 現在のフェイズ
 * @returns {Array<{monster: Object, slotIndex: number}>}
 */
export function findMiraiYochiMonsters(field: FieldArray, phase: number): MiraiYochiMonster[] {
  const results: MiraiYochiMonster[] = [];
  field.forEach((monster, idx) => {
    if (canActivateMiraiYochi(monster, phase)) {
      results.push({ monster: monster as FieldMonster, slotIndex: idx });
    }
  });
  return results;
}

// =============================================================================
// 【飛行】【対空】関連
// =============================================================================

/**
 * 【飛行】を持つカードか判定
 * @param card - カードオブジェクト（KeywordCheckable互換）
 * @returns 飛行を持つかどうか
 */
export function hasHikou(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.HIKOU);
}

/**
 * 【対空】を持つカードか判定
 * @param card - カードオブジェクト（KeywordCheckable互換）
 * @returns 対空を持つかどうか
 */
export function hasTaiku(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.TAIKU);
}

/**
 * 地上モンスターか判定（飛行を持たない）
 * @param card - カードオブジェクト（KeywordCheckable互換）
 * @returns 地上モンスターかどうか
 */
export function isGroundMonster(card: KeywordCheckable | null): boolean {
  if (!card) return false;
  return !hasHikou(card);
}

/**
 * フィールド上に飛行または対空を持つモンスターがいるか
 * （飛行モンスターのダイレクトアタックを迎撃できるモンスターがいるか）
 * @param field - フィールド配列
 * @returns 飛行/対空モンスターがいるかどうか
 */
export function hasAirDefender(field: FieldArray): boolean {
  return field.some(monster =>
    monster && (hasHikou(monster) || hasTaiku(monster))
  );
}

/**
 * フィールド上の飛行モンスター数を取得
 * @param field - フィールド配列
 * @returns 飛行モンスターの数
 */
export function countHikouMonsters(field: FieldArray): number {
  return field.filter(monster => monster && hasHikou(monster)).length;
}

/**
 * フィールド上に地上モンスターがいるか
 * @param field - フィールド配列
 * @returns 地上モンスターがいるかどうか
 */
export function hasGroundMonster(field: FieldArray): boolean {
  return field.some(monster => monster && isGroundMonster(monster));
}

// =============================================================================
// 【潜航】関連
// =============================================================================

/**
 * 【潜航】を持つカードか判定
 * @param card - カードオブジェクト
 * @returns 潜航を持つかどうか
 */
export function hasSenkou(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.SENKOU);
}

/**
 * 【潜航】の対象カードID一覧
 */
export const SENKOU_CARD_IDS = [
  'C0000520', // ディープシーカー
  'C0000522', // 深海潜伏者
  'C0000524', // 深淵の番人
  'C0000528', // 深海の捕食者
  'C0000530', // 深淵竜レヴィアタン
  'C0000531', // 深淵の支配者ヴェルゼファール
  'C0000532', // 深海神殿の守護者
];

// =============================================================================
// 【深淵の牙】関連
// =============================================================================

/**
 * 【深淵の牙】を持つカードか判定
 * @param card - カードオブジェクト
 * @returns 深淵の牙を持つかどうか
 */
export function hasShihenNoKiba(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.SHINEN_NO_KIBA);
}

/**
 * 【深淵の牙】の対象カードID一覧
 * 浮上時に深度×200ダメージ
 */
export const SHINEN_NO_KIBA_CARD_IDS = [
  'C0000528', // 深海の捕食者
  'C0000530', // 深淵竜レヴィアタン
];

/**
 * 【深淵の牙】のダメージを計算
 * @param depth - 浮上時の深度
 * @returns ダメージ量
 */
export function calculateShihenNoKibaDamage(depth: number): number {
  return depth * 200;
}

// =============================================================================
// 【海底の眠り】関連
// =============================================================================

/**
 * 【海底の眠り】を持つカードか判定
 * @param card - カードオブジェクト
 * @returns 海底の眠りを持つかどうか
 */
export function hasKaiteiNoNemuri(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.KAITEI_NO_NEMURI);
}

/**
 * 【海底の眠り】の対象カードID一覧
 * 深海領域に存在する間、毎ターン200LP回復
 */
export const KAITEI_NO_NEMURI_CARD_IDS = [
  'C0000524', // 深淵の番人
  'C0000532', // 深海神殿の守護者
];

/**
 * 【海底の眠り】の回復量
 */
export const KAITEI_NO_NEMURI_HEAL_AMOUNT = 200;

// =============================================================================
// 【暗黒水域】関連
// =============================================================================

/**
 * 【暗黒水域】を持つカードか判定
 * @param card - カードオブジェクト
 * @returns 暗黒水域を持つかどうか
 */
export function hasAnkokuSuiiki(card: KeywordCheckable | null): boolean {
  return hasKeyword(card as KeywordCard | null, KEYWORD_ABILITIES.ANKOKU_SUIIKI);
}

/**
 * 【暗黒水域】の対象カードID一覧
 * 場にいる間、深海領域の深度上昇が+1
 */
export const ANKOKU_SUIIKI_CARD_IDS = [
  'C0000531', // 深淵の支配者ヴェルゼファール
];

/**
 * フィールド上に【暗黒水域】を持つモンスターがいるか
 * @param field - フィールド配列
 * @returns 暗黒水域モンスターがいるかどうか
 */
export function hasAnkokuSuiikiOnField(field: FieldArray): boolean {
  return field.some(monster => monster && hasAnkokuSuiiki(monster));
}

/**
 * フィールド上の【暗黒水域】を持つモンスターの数
 * @param field - フィールド配列
 * @returns 暗黒水域モンスターの数
 */
export function countAnkokuSuiikiMonsters(field: FieldArray): number {
  return field.filter(monster => monster && hasAnkokuSuiiki(monster)).length;
}

// =============================================================================
// flightHelpers エクスポート
// =============================================================================

export * from './flightHelpers';

